import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * ScrollToTop — global scroll behavior for client-side navigation.
 *
 * Mounted once at the App root. Behaviour:
 *   - PUSH / REPLACE (link click, programmatic navigate): scroll to (0, 0)
 *     unless the URL has a `#hash`, in which case scroll the matching element
 *     into view.
 *   - POP (browser back/forward): do nothing — let the browser's native
 *     scroll restoration handle it. The browser saves the scroll position
 *     of each history entry on navigation away and restores it on POP, so
 *     by skipping POP here the user lands back exactly where they left off.
 *
 * React Router's <BrowserRouter> doesn't restore scroll automatically, which
 * is why every link click was preserving the prior page's scroll offset.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === "POP") return;

    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: "instant", block: "start" });
        return;
      }
    }

    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash, navigationType]);

  return null;
};

export default ScrollToTop;
